import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { parasutApi, errMsg } from "../../lib/parasutApi";
import { VAT_RATES } from "./invoiceDefaults";

const inp = "h-8 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/40";
const th = "px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500";

function Card({ title, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.14em] text-indigo-700">{title}</h3>
      {children}
    </section>
  );
}

function ChannelsCard({ r, set }) {
  const upd = (i, k, v) => set({ ...r, channels: r.channels.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });
  return (
    <Card title="Kanallar · acenta payı ve cari kodu">
      <table className="w-full"><thead><tr><th className={th}>Kanal</th><th className={th}>Eşleşme (kanal adında geçen)</th><th className={th}>Cari öneki</th><th className={th}>Acenta payı %</th><th className={th}>Ürüne özel oran (kelime,kelime:%)</th><th className={th}>Fiyat indirimi %</th><th className={th}>Faturadaki site</th><th className={th}>Bölme</th></tr></thead>
        <tbody>{r.channels.map((c, i) => (
          <tr key={i} data-testid={`rule-channel-${i}`}>
            <td className="p-1"><input className={`${inp} w-full`} value={c.name} onChange={(e) => upd(i, "name", e.target.value)} /></td>
            <td className="p-1"><input className={`${inp} w-full`} value={c.match} disabled={c.match === "*"} onChange={(e) => upd(i, "match", e.target.value)} /></td>
            <td className="p-1"><input data-testid={`rule-channel-prefix-${i}`} className={`${inp} w-20`} value={c.prefix} onChange={(e) => upd(i, "prefix", e.target.value.toUpperCase())} /></td>
            <td className="p-1"><input data-testid={`rule-channel-pct-${i}`} type="number" step="0.1" className={`${inp} w-24`} value={c.commission_pct} onChange={(e) => upd(i, "commission_pct", e.target.value)} /></td>
            <td className="p-1"><input data-testid={`rule-channel-product-pct-${i}`} placeholder="masal,sunset:30" className={`${inp} w-full`} value={c.product_pct || ""} onChange={(e) => upd(i, "product_pct", e.target.value)} /></td>
            <td className="p-1"><input data-testid={`rule-channel-discount-${i}`} type="number" step="0.1" className={`${inp} w-16`} value={c.price_discount_pct || 0} onChange={(e) => upd(i, "price_discount_pct", e.target.value)} /></td>
            <td className="p-1"><input data-testid={`rule-channel-site-${i}`} placeholder="otomatik" className={`${inp} w-full`} value={c.site || ""} onChange={(e) => upd(i, "site", e.target.value)} /></td>
            <td className="p-1"><select data-testid={`rule-channel-mode-${i}`} className={inp} value={c.split_mode || "commission"} onChange={(e) => upd(i, "split_mode", e.target.value)}>
              <option value="commission">Komisyon + sabit</option><option value="percent">Oranla</option></select></td>
          </tr>))}
        </tbody></table>
      <p className="mt-2 text-[11px] text-slate-400">"*" satırı diğer tüm kanallar (direkt, manuel) için geçerlidir. Ürüne özel oran: ürün adında kelime geçerse o oran kullanılır, birden fazla grup ";" ile ayrılır. Cari adı: {r.contact_code_base}.{r.channels[0]?.prefix}001 Ad Soyad</p>
    </Card>
  );
}

function AmountsCard({ r, set }) {
  const updAmt = (ti, key, v) => set({ ...r, tiers: r.tiers.map((t, j) => (j === ti ? { ...t, amounts: { ...t.amounts, [key]: v } } : t)) });
  const updComp = (ci, k, v) => set({ ...r, components: r.components.map((c, j) => (j === ci ? { ...c, [k]: v } : c)) });
  return (
    <Card title="Sabit kalemler · kişi başı, KDV dahil">
      <table className="w-full"><thead><tr><th className={th}>Kalem</th><th className={th}>KDV</th>{r.tiers.map((t) => <th key={t.key} className={th}>{t.label}</th>)}</tr></thead>
        <tbody>{r.components.map((c, ci) => (
          <tr key={c.key} data-testid={`rule-comp-${c.key}`}>
            <td className="p-1"><input className={`${inp} w-full`} value={c.label} onChange={(e) => updComp(ci, "label", e.target.value)} />
              <div className="text-[10px] text-slate-400 px-1">{c.when === "transfer" ? "sadece transferli" : c.when === "alcohol" ? "sadece alkollü" : "her zaman"}</div></td>
            <td className="p-1"><select className={inp} value={c.vat} onChange={(e) => updComp(ci, "vat", Number(e.target.value))}>{VAT_RATES.map((v) => <option key={v} value={v}>%{v}</option>)}</select></td>
            {r.tiers.map((t, ti) => (
              <td key={t.key} className="p-1"><input data-testid={`rule-amt-${t.key}-${c.key}`} type="number" step="0.01" className={`${inp} w-24`} value={t.amounts?.[c.key] ?? 0} onChange={(e) => updAmt(ti, c.key, e.target.value)} /></td>
            ))}
          </tr>))}
        </tbody></table>
    </Card>
  );
}

const COMBOS = [["none", "Transfersiz alkolsüz"], ["t", "Transferli alkolsüz"], ["a", "Transfersiz alkollü"], ["ta", "Transferli alkollü"]];

function PctCard({ r, set }) {
  const tables = r.pct_tables || {};
  const upd = (tk, ck, key, v) => set({ ...r, pct_tables: { ...tables, [tk]: { ...tables[tk], [ck]: { ...(tables[tk]?.[ck] || {}), [key]: v } } } });
  return (
    <Card title="Oranla bölme · kendi site kanalları (satış fiyatının %'si)">
      {Object.keys(tables).sort((x, y) => r.tiers.findIndex((t) => t.key === x) - r.tiers.findIndex((t) => t.key === y)).map((tk) => (
        <table key={tk} className="w-full mb-3"><thead><tr><th className={th}>{(r.tiers.find((t) => t.key === tk) || {}).label || tk}</th>{r.components.map((c) => <th key={c.key} className={th}>{(r.tiers.find((t) => t.key === tk) || {}).labels?.[c.key] || c.label}</th>)}<th className={th}>Toplam</th></tr></thead>
          <tbody>{COMBOS.map(([ck, label]) => {
            const row = tables[tk]?.[ck] || {};
            const sum = r.components.reduce((a, c) => a + (Number(row[c.key]) || 0), 0);
            return (
              <tr key={ck} data-testid={`rule-pct-${tk}-${ck}`}>
                <td className="p-1 text-[12px] text-slate-600">{label}</td>
                {r.components.map((c) => <td key={c.key} className="p-1"><input data-testid={`rule-pct-${tk}-${ck}-${c.key}`} type="number" step="0.01" className={`${inp} w-20`} value={row[c.key] ?? 0} onChange={(e) => upd(tk, ck, c.key, e.target.value)} /></td>)}
                <td className={`p-1 text-[12px] font-semibold ${Math.abs(sum - 100) > 0.05 ? "text-red-600" : "text-emerald-700"}`}>%{sum.toFixed(2)}</td>
              </tr>);
          })}</tbody></table>))}
      <p className="text-[11px] text-slate-400">Bölme sütunu "Oranla" olan kanallarda komisyon ve hizmet satırı olmaz. Yuvarlama farkı son satıra eklenir, toplam her zaman satış fiyatına eşit olur. Tabloda olmayan tur türleri (sunset vb.) normal kuralla bölünür.</p>
    </Card>
  );
}

function GeneralCard({ r, set }) {
  const f = (k, label, w = "w-full") => (
    <label className="flex flex-col gap-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <input data-testid={`rule-${k}`} className={`${inp} ${w}`} value={r[k] ?? ""} onChange={(e) => set({ ...r, [k]: e.target.value })} /></label>
  );
  return (
    <Card title="Genel">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {f("commission_label", "Acenta payı satır adı")}
        {f("commission_note", "Acenta payı açıklaması")}
        {f("service_label", "Kalan (hizmet) satır adı")}
        {f("contact_code_base", "Cari kod başı")}
        {f("exemption_code", "KDV %0 istisna kodu")}
        {f("exemption_reason", "İstisna açıklaması")}
        {f("foreign_tax_number", "Yabancı müşteri VKN")}
      </div>
      <p className="mt-2 text-[11px] text-slate-400">Fatura = acenta payı (%0) + sabit kalemler + kalan tutar "{r.service_label}" (%0). Tur türü, transfer ve alkol üründen otomatik algılanır, fatura ekranında değiştirilebilir.</p>
    </Card>
  );
}

export function InvoiceRules() {
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let alive = true; // geç gelen (StrictMode çift) yanıt kullanıcının düzenlemesini ezmesin
    parasutApi.rules().then((v) => { if (alive) setR(v); }).catch((e) => { if (alive) toast.error(errMsg(e)); });
    return () => { alive = false; };
  }, []);
  const save = async () => {
    setSaving(true);
    const num = (v) => Number(v) || 0;
    const body = {
      ...r,
      channels: r.channels.map((c) => ({ ...c, commission_pct: num(c.commission_pct), price_discount_pct: num(c.price_discount_pct || 0) })),
      tiers: r.tiers.map((t) => ({ ...t, amounts: Object.fromEntries(Object.entries(t.amounts || {}).map(([k, v]) => [k, num(v)])) })),
      pct_tables: Object.fromEntries(Object.entries(r.pct_tables || {}).map(([tk, cm]) => [tk, Object.fromEntries(Object.entries(cm).map(([ck, row]) => [ck, Object.fromEntries(Object.entries(row).map(([k, v]) => [k, num(v)]))]))])),
    };
    try { setR(await parasutApi.saveRules(body)); toast.success("Fatura kuralları kaydedildi"); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  if (!r) return <div className="py-10 text-center text-slate-400"><Loader2 className="inline animate-spin" size={16} /> Yükleniyor…</div>;
  return (
    <div data-testid="invoice-rules" className="flex flex-col gap-4">
      <ChannelsCard r={r} set={setR} />
      <AmountsCard r={r} set={setR} />
      <PctCard r={r} set={setR} />
      <GeneralCard r={r} set={setR} />
      <div className="flex justify-end">
        <button data-testid="rules-save" disabled={saving} onClick={save} className="inline-flex items-center gap-1.5 h-9 px-5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Kaydet
        </button>
      </div>
    </div>
  );
}
